import React, { useState, useEffect } from 'react';
import {
  makeStyles, Typography, Checkbox, Box, Modal, Button, Snackbar, Grid, TextField,
  Table, TableHead, TableBody, TableRow, TableCell
} from '@material-ui/core';
import { Stack } from '@mui/material';
import { api_call, console_log, isGpsMT2VProtocolDevice } from '../helpers/untils';
import { DEVICE_TYPE } from '../config/constant';

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '90%',
  maxWidth: '780px',
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 3,
};

const useStyles = makeStyles(() => ({
  formContainer: {
    display: 'block',
    width: '100%',
  },
  buttons: {
    display: 'flex',
    justifyContent: 'space-evenly',
    '& > *': {
      flexBasis: '33%',
    },
  },
  voltInput: {
    width: '60px',
    margin: '0 6px',
  },
  centerCell: {
    textAlign: 'center',
  },
}));

const DEFAULT_UNDER_VOLT = 11;
const DEFAULT_OVER_VOLT = 14;

const ACTIONS = [
  { key: 'Lock', label: 'Lock' },
  { key: 'Unlock', label: 'Unlock' },
  { key: 'Kill', label: 'Killswitch' },
  { key: 'Horn', label: 'Horn' },
  { key: 'Light', label: 'Flash Lights' },
  { key: 'PushNotification', label: 'Push Notification' },
  { key: 'EmailNotification', label: 'Email Notification' },
];

const buildDefaultFormData = () => ({
  underVolt: DEFAULT_UNDER_VOLT,
  overVolt: DEFAULT_OVER_VOLT,
  underEmail: "",
  overEmail: "",
  under: ACTIONS.reduce((acc, a) => ({ ...acc, [a.key]: false }), {}),
  over: ACTIONS.reduce((acc, a) => ({ ...acc, [a.key]: false }), {}),
});

const getFilteredActions = (device) => {
  if (device.deviceType === DEVICE_TYPE.MOOVETRAX) {
    if (isGpsMT2VProtocolDevice(device.uniqueId)) {
      return ACTIONS
    } else {
      return ACTIONS.filter((i) => (i.key === 'Kill' || i.key === 'PushNotification' || i.key === 'EmailNotification'))
    }
  } else {
    return ACTIONS.filter((i) => (i.key !== 'Light'))
  }
}

const LowHighBatteryActionModal = (props) => {
  const classes = useStyles();
  const { open, setOpen, item, setItem } = props;

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [apiCalling, setApiCalling] = useState(false);
  const [formData, setFormData] = useState(buildDefaultFormData());

  const handleModalClose = () => {
    setOpen(false);
  };

  useEffect(() => {
    const defaults = buildDefaultFormData();
    try {
      if (item?.lowBatterySetting) {
        const saved = JSON.parse(item.lowBatterySetting);
        setFormData({
          underVolt: saved.underVolt ?? defaults.underVolt,
          overVolt: saved.overVolt ?? defaults.overVolt,
          underEmail: saved.underEmail ?? defaults.underEmail,
          overEmail: saved.overEmail ?? defaults.overEmail,
          under: { ...defaults.under, ...(saved.under || {}) },
          over: { ...defaults.over, ...(saved.over || {}) },
        });
        return;
      }
    } catch (e) {
      console_log('LowHighBatteryActionModal parse error:::', e);
    }
    setFormData(defaults);
  }, [item, open]);

  const handleCheck = (column, key, event) => {
    setFormData((prev) => ({
      ...prev,
      [column]: { ...prev[column], [key]: event.target.checked },
    }));
  };

  const handleVoltChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleEmailChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveSetting = async () => {
    const payload = {
      device_id: item.id,
      lowBatterySetting: JSON.stringify(formData),
    };
    setApiCalling(true);
    const response = await api_call('/api/devices/saveLowBatterySetting', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    setApiCalling(false);
    if (response.ok) {
      const apiData = await response.json();
      if (apiData?.device && setItem) {
        setItem(apiData.device);
      }
      setToastMessage('Saved successfully!');
      setSnackbarOpen(true);
      setTimeout(() => {
        handleModalClose();
      }, 1000);
    } else {
      const apiError = response;
      console_log('apiError:::', apiError);
      setToastMessage(apiError.error);
      setSnackbarOpen(true);
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleModalClose}
      aria-labelledby="low-battery-modal-title"
      aria-describedby="low-battery-modal-description"
    >
      <Box>
        <Box sx={modalStyle}>
          <Snackbar
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            open={snackbarOpen}
            onClose={() => { setSnackbarOpen(false); }}
            autoHideDuration={1500}
            message={toastMessage}
          />
          <Grid container justifyContent="space-between" alignItems="center" spacing={1}>
            <Grid item>
              <Typography variant="h6">Low Battery Action</Typography>
            </Grid>
          </Grid>

          <div className="mt-4">
            <div className={classes.formContainer} style={{ maxHeight: '460px', overflowY: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell />
                    <TableCell className={classes.centerCell}>
                      When volt goes under
                      <TextField
                        type="number"
                        size="small"
                        variant="outlined"
                        className={classes.voltInput}
                        value={formData.underVolt}
                        onChange={(e) => handleVoltChange('underVolt', e.target.value)}
                        inputProps={{ style: { textAlign: 'center', padding: '2px 6px 6px 6px' } }}
                      />
                      Volt
                    </TableCell>
                    <TableCell className={classes.centerCell}>
                      When volt goes over
                      <TextField
                        type="number"
                        size="small"
                        variant="outlined"
                        className={classes.voltInput}
                        value={formData.overVolt}
                        onChange={(e) => handleVoltChange('overVolt', e.target.value)}
                        inputProps={{ style: { textAlign: 'center', padding: '2px 6px 6px 6px' } }}
                      />
                      Volt
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {getFilteredActions(item).map((action) => (
                    <TableRow key={action.key}>
                      <TableCell>{action.label}</TableCell>
                      <TableCell className={classes.centerCell}>
                        <Stack direction={`row`} justifyContent={`space-between`} alignItems={`center`}>
                          <Checkbox
                            checked={!!formData.under[action.key]}
                            onChange={(e) => handleCheck('under', action.key, e)}
                          />
                          {
                            (action.key === 'EmailNotification') ? (
                              <>
                                <TextField
                                  type="text"
                                  size="small"
                                  variant="outlined"
                                  placeholder="separate with comma"
                                  value={formData.underEmail}
                                  onChange={(e) => handleEmailChange('underEmail', e.target.value)}
                                />
                              </>
                            ) : (
                              <div></div>
                            )
                          }
                        </Stack>
                      </TableCell>
                      <TableCell className={classes.centerCell}>
                        <Stack direction={`row`} justifyContent={`space-between`} alignItems={`center`}>
                          <Checkbox
                            checked={!!formData.over[action.key]}
                            onChange={(e) => handleCheck('over', action.key, e)}
                          />
                          {
                            (action.key === 'EmailNotification') ? (
                              <>
                                <TextField
                                  type="text"
                                  size="small"
                                  variant="outlined"
                                  placeholder="separate with comma"
                                  value={formData.overEmail}
                                  onChange={(e) => handleEmailChange('overEmail', e.target.value)}
                                />
                              </>
                            ) : (
                              <div></div>
                            )
                          }
                        </Stack>

                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            <div className={classes.buttons} style={{ paddingTop: '15px', width: '100%', margin: 'auto' }}>
              <Button type="button" color="primary" variant="contained" onClick={() => handleSaveSetting()} disabled={apiCalling}>
                Save
              </Button>
              <Button type="button" color="primary" variant="outlined" onClick={() => setOpen(false)}>
                Close
              </Button>
            </div>
          </div>
        </Box>
      </Box>
    </Modal>
  );
};

export default LowHighBatteryActionModal;
